import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppAssistRetry } from "./AppAssistRetry";
import { AppAssistAnswer, AppAssistApi } from "./AppAssistApi";
import { AppUpdateEvery } from "./AppUpdateEvery";

// AppAssistKeep - "Keep this" on a table or chart in an AI answer (owner-approved 2026-09-27): the button's words,
// the notes it leaves on the block's own note line, and the one request in flight per block. `AppAssistPanel`
// draws the button beside "Copy table" / "Save chart"; the service keeps the block in the contact's `pins.json`
// (`PinRun`) and the page shows it under Insights, "Kept from AI", after the next update.
//
// ⛔ THE REQUEST CARRIES NO MARKUP - only the saved answer's item id and the block's number (`AppAssistApi.PinBody`);
// the service re-derives the block from the SAVED answer. ⛔ NOTHING HERE SENDS A MESSAGE.
// ⚠ OPTIMISTIC NOTE, HONEST WORDS: the click says "Keeping…" at once; the service's answer says "Kept - it will be
// under Insights after the next update" (the page is rebuilt on the refresh cycle, ~15 minutes - the drawer is NOT
// changed in place: a pin drawn into the frame by the app would be markup the page never cleaned). A refusal is the
// service's own sentence in ink (law 1: validation), never the handler.
// ⚠ Kept blocks are remembered for the VISIT (`Kept`, keyed page + item + block) so a repaint shows "Kept" again;
// a second press of a kept block asks nothing.
// ⚠ Pure of browser globals - the Bridge compiles the panel (`AssistContract.test.ts`).

/** What a keep call touches: the block's note line and its button. */
export interface IKeepNode {
    textContent: string | null;
    setAttribute(Name: string, Value: string): void;
}

/** Who can post the pin again after a `retry` refusal (the panel's host). `Later` absent = no re-ask. */
export interface IKeepPoster {
    Post(Body: string, Done: (Status: number, Text: string) => void): void;
    Later?(Ms: number, Then: () => void): void;
}

/** One "Keep this" in flight - answered once, onto that block's own note line and button. */
export class AppAssistKeepCall {
    /** Quiet re-asks after `retry` refusals (the archive still being restored after a restart, 2026-09-29). */
    public static readonly MaxAgain: number = AppAssistRetry.MaxAgain;
    private Key: string;
    private Note: IKeepNode;
    private Button: IKeepNode | null;
    private Retry: AppAssistRetry = new AppAssistRetry();
    private Poster: IKeepPoster | null;
    private Body: string;

    public constructor(Key: string, Note: IKeepNode, Button: IKeepNode | null, Poster: IKeepPoster | null = null, Body: string = "") {
        this.Key = Key;
        this.Note = Note;
        this.Button = Button;
        this.Poster = Poster;
        this.Body = Body;
    }

    /** `AppAssistDone`: "" from `PinWhy` = kept; a `retry` refusal = asked again quietly; anything else is the sentence. */
    public OnDone = (Status: number, Text: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Retry.IsEnded() === true) { return; }
            // ⭐ THE RESTART WINDOW: "Getting your archive ready…" and the same body again - never the final sentence.
            const Heard: AppAssistAnswer = AppAssistApi.Parse(Status, Text, AppAssistApi.LostWhy());
            const Wait: number = this.Retry.WaitFor(Heard);
            if (Wait > 0 && this.Poster !== null
                && this.Poster.Later !== undefined && this.Body.length > 0) {
                this.Retry.Schedule(Wait, this.Poster, this.Again, this.Readying);
                return;
            }
            this.Retry.End();
            delete AppAssistKeep.Busy[this.Key];
            const Why: string = AppAssistApi.PinWhy(Status, Text);
            if (Why.length > 0) {
                this.Note.textContent = Why;
                if (this.Button !== null) { this.Button.textContent = AppAssistKeep.Label(); }
                return;
            }
            AppAssistKeep.Kept[this.Key] = true;
            this.Note.textContent = AppAssistKeep.KeptNote();
            if (this.Button !== null) { this.Button.textContent = AppAssistKeep.KeptLabel(); this.Button.setAttribute("disabled", ""); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistKeepCall.OnDone");
        }
    };

    // DOCUMENTED EXCEPTION: owner-invoked hook. ERROR-SUPPRESSED-JUSTIFIED:
    // note failures propagate to the original OnDone catch; its gate runs before this hook.
    private Readying = (): void => {
        this.Note.textContent = AppAssistKeep.ReadyingNote();
    };

    /** The same pin, posted again. */
    private Again = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Retry.IsEnded() === true || this.Poster === null) { return; }
            this.Poster.Post(this.Body, this.OnDone);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistKeepCall.Again");
        }
    };
}

export class AppAssistKeep {
    /** The log button's `ActAttr` value. Its `IndexAttr` is the BLOCK number in the panel's `Exports`. */
    public static readonly Act: string = "keepblock";
    /** page|item|block -> kept on this visit. */
    public static Kept: Record<string, boolean> = {};
    /** page|item|block -> a request is in flight. */
    public static Busy: Record<string, boolean> = {};

    /** The visit's key for one block. DOCUMENTED EXCEPTION: pure string join; cannot throw. */
    public static KeyOf = (Page: string, Item: string, Block: number): string => {
        return nz(Page) + "|" + nz(Item) + "|" + String(Block);
    };

    /** DOCUMENTED EXCEPTION: pure constant - no input, cannot throw. */
    public static Label = (): string => { return "Keep this"; };
    /** DOCUMENTED EXCEPTION: pure constant - no input, cannot throw. */
    public static KeptLabel = (): string => { return "Kept"; };
    /** DOCUMENTED EXCEPTION: pure constant - no input, cannot throw. */
    public static Aria = (): string => { return "Keep this in the chat's Insights, under Kept from AI"; };
    /** DOCUMENTED EXCEPTION: pure constant - no input, cannot throw. */
    public static KeepingNote = (): string => { return "Keeping…"; };
    /** DOCUMENTED EXCEPTION: pure constant - no input, cannot throw. */
    public static ReadyingNote = (): string => { return "Getting your archive ready…"; };
    /** DOCUMENTED EXCEPTION: pure constant - no input, cannot throw. */
    public static KeptNote = (): string => {
        return "Kept - it will be under Insights, Kept from AI, after the next update (" + AppUpdateEvery.Within + ").";
    };
    /** DOCUMENTED EXCEPTION: pure constant - no input, cannot throw. */
    public static NotSavedNote = (): string => {
        return "This answer is not in the chat's saved history, so it can't be kept - ask again and keep the new one.";
    };
    /** DOCUMENTED EXCEPTION: pure constant - no input, cannot throw. */
    public static SignInNote = (): string => {
        return "Sign in on this browser to keep tables and charts.";
    };
}
